import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { useGroups, useJobs, useEntities, useDocuments } from '../../hooks/useCrmData';
import { getAccessToken } from '../../authUtils';
import { useRole } from '../../context/UserContext';
import type { FolderItem, Group, Job } from '../../types/crm';
import {
  COLORS, GRADIENTS, SHADOWS, Icons, Btn, Dropdown, TypeBadge, GroupTypeBadge, StatusBadge,
  TH, TD, ErrorBlock, useToast,
  PageHeader, EmptyState, SkeletonTable,
} from '../ui/crm-ui';
import CreateGroupModal from './modals/CreateGroupModal';
import CreateJobModal from './modals/CreateJobModal';
import CreateEntityModal from './modals/CreateEntityModal';
import ImportWizardModal from './modals/ImportWizardModal';
import BulkUploadModal from './modals/BulkUploadModal';
import DeleteConfirmModal from './modals/DeleteConfirmModal';
import DocumentAssistancePanel from '../DocumentAssistancePanel';

type Level = 'topGroups' | 'corpChildren' | 'jobs' | 'entities' | 'docs';

type DeleteMode = 'corporate-group' | 'family-group' | 'job' | 'entity' | 'document';

type ModalState =
  | null
  | { kind: 'add-group'; lockedType?: 'family_group'; defaultParentCorpId?: number }
  | { kind: 'add-job';   groupId: number; groupName?: string }
  | { kind: 'add-entity'; jobId: number; jobName?: string }
  | { kind: 'import' }
  | {
      kind: 'delete';
      deleteMode: DeleteMode;
      itemId: number | string;
      jobId?: number;
      itemName: string;
      documentMoveDestination?: string;
      childSummary?: string;
    };

interface Props {
  baseApiUrl: string;
}

function is404Error(error: string | null): boolean {
  return !!error?.includes('404');
}

function fileExt(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 ? name.slice(i + 1).toLowerCase() : '';
}

function formatDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function formatSize(bytes?: number): string {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const ClientsView: React.FC<Props> = ({ baseApiUrl }) => {
  const toast = useToast();
  const { role } = useRole();
  const canEdit = role === 'developer';
  const canDelete = true; // All authenticated users can delete documents

  const [level, setLevel] = useState<Level>('topGroups');
  const [groupSearch, setGroupSearch] = useState('');
  const [corpGroupId, setCorpGroupId] = useState<number | null>(null);
  const [activeGroupId, setActiveGroupId] = useState<number | null>(null);
  const [activeJobId, setActiveJobId] = useState<number | null>(null);
  const [activeEntityId, setActiveEntityId] = useState<number | null>(null);
  const [showAgentPanel, setShowAgentPanel] = useState(false);

  const [modal, setModal] = useState<ModalState>(null);
  const [preparingDelete, setPreparingDelete] = useState<number | null>(null);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Top-level groups: corp groups + standalone family groups (parent_id is null).
  const topGroups = useGroups(baseApiUrl, { parentId: null });

  // Family groups nested under the active corp group (when level === 'corpChildren').
  const corpChildren = useGroups(
    baseApiUrl,
    corpGroupId != null ? { parentId: corpGroupId } : { parentId: -1 }, // -1 = nothing
  );

  // Jobs whose parent is the active corp group (direct jobs under a corp).
  const corpDirectJobs = useJobs(baseApiUrl, corpGroupId);

  // Jobs at the 'jobs' level — under activeGroupId (which may be the corp itself,
  // a nested family group, or a standalone family group).
  const jobsForActiveGroup = useJobs(baseApiUrl, activeGroupId);

  const entities = useEntities(baseApiUrl, activeJobId);

  const corpGroup = useMemo(
    () => topGroups.data?.find((g) => g.id === corpGroupId) ?? null,
    [topGroups.data, corpGroupId],
  );
  const activeGroup = useMemo<Group | null>(() => {
    if (activeGroupId == null) return null;
    return (
      topGroups.data?.find((g) => g.id === activeGroupId) ??
      corpChildren.data?.find((g) => g.id === activeGroupId) ??
      null
    );
  }, [topGroups.data, corpChildren.data, activeGroupId]);

  const activeJob = useMemo<Job | null>(() => {
    if (activeJobId == null) return null;
    return (
      jobsForActiveGroup.data?.find((j) => j.id === activeJobId) ??
      corpDirectJobs.data?.find((j) => j.id === activeJobId) ??
      null
    );
  }, [jobsForActiveGroup.data, corpDirectJobs.data, activeJobId]);

  const activeEntityRow = useMemo(
    () => entities.data?.find((e) => e.entity_id === activeEntityId) ?? null,
    [entities.data, activeEntityId],
  );

  const allJobsForSwitcher = useMemo(() => {
    const seen = new Set<number>();
    const merged: Job[] = [];
    for (const j of [...(jobsForActiveGroup.data ?? []), ...(corpDirectJobs.data ?? [])]) {
      if (!seen.has(j.id)) { seen.add(j.id); merged.push(j); }
    }
    return merged;
  }, [jobsForActiveGroup.data, corpDirectJobs.data]);

  const docsFolderId = (() => {
    if (level === 'topGroups') return null;
    if (level === 'corpChildren') return corpGroup?.sharepoint_folder_id ?? null;
    if (level === 'jobs')         return activeGroup?.sharepoint_folder_id ?? null;
    if (level === 'entities')     return activeJob?.sharepoint_folder_id ?? null;
    if (level === 'docs') {
      return activeEntityRow?.entity_sharepoint_folder_id
          ?? activeJob?.sharepoint_folder_id
          ?? null;
    }
    return null;
  })();
  const documents = useDocuments(baseApiUrl, docsFolderId);
  const fileItems: FolderItem[] = (documents.data || []).filter((d) => d.type === 'file');

  // ─── Drill / back transitions ─────────────────────────────────────────────

  function drillFromTopGroup(g: Group) {
    if (g.type === 'corporate_group') {
      setCorpGroupId(g.id); setActiveGroupId(null); setActiveJobId(null); setActiveEntityId(null);
      setShowAgentPanel(false); setLevel('corpChildren');
    } else {
      // Standalone family group — straight to jobs
      setCorpGroupId(null); setActiveGroupId(g.id); setActiveJobId(null); setActiveEntityId(null);
      setShowAgentPanel(false); setLevel('jobs');
    }
  }

  function drillIntoJob(j: Job) {
    setActiveJobId(j.id); setActiveEntityId(null);
    // If the job belonged to a corp directly (no family group), set activeGroupId so the
    // back-button to 'jobs' lands somewhere meaningful.
    if (activeGroupId == null && corpGroupId != null) setActiveGroupId(corpGroupId);
    setShowAgentPanel(false); setLevel('entities');
  }

  function drillIntoEntity(id: number | null) {
    setActiveEntityId(id); setShowAgentPanel(false); setLevel('docs');
  }

  function goBack(to: Level) {
    setShowAgentPanel(false);
    if (to === 'topGroups') {
      setCorpGroupId(null); setActiveGroupId(null); setActiveJobId(null); setActiveEntityId(null);
      setLevel('topGroups');
    } else if (to === 'corpChildren') {
      setActiveGroupId(null); setActiveJobId(null); setActiveEntityId(null);
      setLevel('corpChildren');
    } else if (to === 'jobs') {
      setActiveJobId(null); setActiveEntityId(null);
      if (corpGroupId != null && (activeGroupId == null || activeGroupId === corpGroupId)) {
        setLevel('corpChildren');
      } else {
        setLevel('jobs');
      }
    } else if (to === 'entities') {
      setActiveEntityId(null);
      setLevel('entities');
    } else {
      setLevel(to);
    }
  }

  // ─── Heading ──────────────────────────────────────────────────────────────

  const heading =
    level === 'topGroups' ? 'Client Groups' :
    level === 'corpChildren' ? corpGroup?.name ?? 'Group' :
    level === 'jobs' ? activeGroup?.name ?? 'Jobs' :
    level === 'entities' ? activeJob?.name ?? 'Entities' :
    activeEntityRow?.entity_name || activeJob?.name || 'Documents';

  // ─── Handlers for create modals ──────────────────────────────────────────

  function handleGroupCreated(_id: number) {
    setModal(null);
    toast.show('Group created');
    if (level === 'topGroups') topGroups.refetch();
    else if (level === 'corpChildren') corpChildren.refetch();
  }

  function handleJobCreated(_id: number) {
    setModal(null);
    toast.show('Job created');
    if (level === 'corpChildren') corpDirectJobs.refetch();
    else jobsForActiveGroup.refetch();
  }

  function handleEntityCreated(_id: number) {
    setModal(null);
    toast.show('Entity linked to job');
    entities.refetch();
  }

  // ─── Delete handlers ──────────────────────────────────────────────────────

  async function handleTrashGroup(g: Group) {
    if (g.type === 'corporate_group') {
      setPreparingDelete(g.id);
      try {
        const token = await getAccessToken();
        if (!token) { setPreparingDelete(null); return; }
        const hdrs = { Authorization: `Bearer ${token}` };
        const [allGroupsRes, directJobsRes] = await Promise.all([
          axios.get<Group[]>(`${baseApiUrl}/api/groups`, { headers: hdrs }),
          axios.get<Job[]>(`${baseApiUrl}/api/job-records`, { headers: hdrs, params: { group_id: g.id } }),
        ]);
        const childFamilyGroups = allGroupsRes.data.filter((x) => x.parent_id === g.id);
        const parts: string[] = [];
        if (childFamilyGroups.length > 0) parts.push(`${childFamilyGroups.length} family group${childFamilyGroups.length !== 1 ? 's' : ''}`);
        if (directJobsRes.data.length > 0) parts.push(`${directJobsRes.data.length} direct job${directJobsRes.data.length !== 1 ? 's' : ''}`);
        setModal({
          kind: 'delete', deleteMode: 'corporate-group',
          itemId: g.id, itemName: g.name,
          documentMoveDestination: 'General Documents',
          childSummary: parts.length > 0 ? parts.join(', ') : undefined,
        });
      } catch {
        setModal({
          kind: 'delete', deleteMode: 'corporate-group',
          itemId: g.id, itemName: g.name,
          documentMoveDestination: 'General Documents',
        });
      } finally {
        setPreparingDelete(null);
      }
    } else {
      // Family group
      const parentName = g.parent_id
        ? (topGroups.data?.find((p) => p.id === g.parent_id)?.name ?? corpGroup?.name ?? 'General Documents')
        : 'General Documents';
      setModal({
        kind: 'delete', deleteMode: 'family-group',
        itemId: g.id, itemName: g.name,
        documentMoveDestination: parentName,
      });
    }
  }

  function handleTrashJob(j: Job) {
    const parentName = activeGroup?.name ?? corpGroup?.name ?? 'General Documents';
    setModal({
      kind: 'delete', deleteMode: 'job',
      itemId: j.id, itemName: j.name,
      documentMoveDestination: parentName,
    });
  }

  function handleTrashEntity(entityId: number, jobId: number, entityName: string) {
    setModal({
      kind: 'delete', deleteMode: 'entity',
      itemId: entityId, jobId, itemName: entityName,
      documentMoveDestination: activeJob?.name ?? 'Job folder',
    });
  }

  function handleTrashDocument(d: FolderItem) {
    setModal({ kind: 'delete', deleteMode: 'document', itemId: d.id, itemName: d.name });
  }

  async function handleDeleteConfirm(confirmMode?: 'group-only' | 'everything') {
    if (!modal || modal.kind !== 'delete') return;
    if (deleting) return;
    setDeleting(true);
    const token = await getAccessToken();
    if (!token) { toast.show('Not authenticated'); setDeleting(false); return; }
    const hdrs = { Authorization: `Bearer ${token}` };
    const { deleteMode, itemId, jobId } = modal;

    try {
      if (deleteMode === 'corporate-group') {
        await axios.delete(`${baseApiUrl}/api/groups/${itemId}`, { headers: hdrs, params: { mode: confirmMode } });
        toast.show('Group deleted');
        setModal(null);
        topGroups.refetch();
        if (level === 'topGroups') setLevel('topGroups');
      } else if (deleteMode === 'family-group') {
        await axios.delete(`${baseApiUrl}/api/groups/${itemId}/family`, { headers: hdrs, params: { mode: confirmMode } });
        toast.show('Family group deleted');
        setModal(null);
        if (level === 'corpChildren') corpChildren.refetch();
        else topGroups.refetch();
      } else if (deleteMode === 'job') {
        await axios.delete(`${baseApiUrl}/api/job-records/${itemId}`, { headers: hdrs });
        toast.show('Job deleted');
        setModal(null);
        jobsForActiveGroup.refetch();
        corpDirectJobs.refetch();
      } else if (deleteMode === 'entity') {
        await axios.delete(`${baseApiUrl}/api/entities/${itemId}/jobs/${jobId}`, { headers: hdrs });
        toast.show('Entity removed');
        setModal(null);
        entities.refetch();
      } else if (deleteMode === 'document') {
        await axios.delete(`${baseApiUrl}/api/documents/${itemId}`, { headers: hdrs });
        toast.show('Document deleted');
        setModal(null);
        documents.refetch();
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
        ?? (err as { message?: string })?.message ?? 'Delete failed';
      toast.show(msg);
      setModal(null);
    } finally {
      setDeleting(false);
    }
  }

  // ─── Sync SharePoint handler ──────────────────────────────────────────────

  async function handleSyncSharePoint() {
    const token = await getAccessToken();
    if (!token) { toast.show('Not authenticated'); return; }
    setSyncing(true);
    toast.show('Sync started…');
    try {
      const res = await axios.post(
        `${baseApiUrl}/api/documents/sync/full-scan`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const { added, updated, deleted } = res.data as { added: number; updated: number; deleted: number };
      toast.show(`Sync complete: ${added} added, ${updated} updated, ${deleted} deleted`);
      topGroups.refetch();
      corpDirectJobs.refetch();
      jobsForActiveGroup.refetch();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } }; message?: string })?.response?.data?.error
        ?? (err as { message?: string })?.message ?? 'Sync failed';
      toast.show(msg);
    } finally {
      setSyncing(false);
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────

  // Build breadcrumb element for the page header
  const crumb = level !== 'topGroups' ? (
    <>
      <span onClick={() => goBack('topGroups')} style={{ cursor: 'pointer', color: COLORS.blue, fontWeight: 500 }}>Clients</span>
      {corpGroup && (
        <>
          {Icons.chevRight(COLORS.mid)}
          <span
            onClick={() => goBack('corpChildren')}
            style={{ cursor: 'pointer', color: level === 'corpChildren' ? COLORS.dark : COLORS.blue, fontWeight: 500 }}
          >{corpGroup.name}</span>
        </>
      )}
      {activeGroup && activeGroup.id !== corpGroupId && (
        <>
          {Icons.chevRight(COLORS.mid)}
          <span
            onClick={() => goBack('jobs')}
            style={{ cursor: 'pointer', color: level === 'jobs' ? COLORS.dark : COLORS.blue, fontWeight: 500 }}
          >{activeGroup.name}</span>
        </>
      )}
      {activeJob && (level === 'entities' || level === 'docs') && (
        <>
          {Icons.chevRight(COLORS.mid)}
          <span
            onClick={() => goBack('entities')}
            style={{ cursor: 'pointer', color: level === 'entities' ? COLORS.dark : COLORS.blue, fontWeight: 500 }}
          >{activeJob.name}</span>
        </>
      )}
      {level === 'docs' && activeEntityRow && (
        <>
          {Icons.chevRight(COLORS.mid)}
          <span style={{ color: COLORS.dark, fontWeight: 500 }}>{activeEntityRow.entity_name}</span>
        </>
      )}
    </>
  ) : undefined;

  const headerIcon =
    level === 'topGroups'   ? Icons.users(COLORS.blue, 22) :
    level === 'corpChildren'? Icons.building(COLORS.blue, 22) :
    level === 'jobs'        ? Icons.building(COLORS.blue, 22) :
    level === 'entities'    ? Icons.users(COLORS.blue, 22) :
                              Icons.docs(COLORS.blue);

  const headerSubtitle =
    level === 'topGroups'    ? 'Browse client groups, jobs, entities and documents.' :
    level === 'corpChildren' ? 'Family groups and direct jobs under this corporate group.' :
    level === 'jobs'         ? 'Jobs and documents for this group.' :
    level === 'entities'     ? 'Entities linked to this job, plus shared documents.' :
                               'Documents stored for this entity.';

  // Header actions
  const headerActions = (
    <>
      {canEdit && level === 'topGroups' && (
        <Btn small onClick={() => setModal({ kind: 'import' })}
          style={{ background: COLORS.blueSoft, color: COLORS.blue, border: `1px solid ${COLORS.blue}40` }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>{Icons.download(COLORS.blue)} Import Clients</span>
        </Btn>
      )}
      {level === 'topGroups' && (
        <>
          <Btn primary small onClick={() => setModal({ kind: 'add-group' })}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>{Icons.plus('#fff')} Add Client Group</span>
          </Btn>
          <Btn
            small
            onClick={handleSyncSharePoint}
            disabled={syncing}
            style={{
              background: COLORS.blueSoft, color: COLORS.blue, border: `1px solid ${COLORS.blue}40`,
              opacity: syncing ? 0.6 : 1, cursor: syncing ? 'not-allowed' : 'pointer',
            }}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              {Icons.refresh(COLORS.blue)} {syncing ? 'Syncing…' : 'Sync SharePoint'}
            </span>
          </Btn>
        </>
      )}

      {canEdit && level === 'corpChildren' && corpGroupId != null && (
        <>
          <Btn primary small onClick={() => setModal({ kind: 'add-group', lockedType: 'family_group', defaultParentCorpId: corpGroupId })}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>{Icons.plus('#fff')} Family Group</span>
          </Btn>
          <Btn primary small onClick={() => setModal({ kind: 'add-job', groupId: corpGroupId, groupName: corpGroup?.name })}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>{Icons.plus('#fff')} Job</span>
          </Btn>
        </>
      )}

      {canEdit && level === 'jobs' && activeGroupId != null && (
        <Btn primary small onClick={() => setModal({ kind: 'add-job', groupId: activeGroupId, groupName: activeGroup?.name })}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>{Icons.plus('#fff')} Add Job</span>
        </Btn>
      )}

      {canEdit && level === 'entities' && activeJobId != null && (
        <Btn primary small onClick={() => setModal({ kind: 'add-entity', jobId: activeJobId, jobName: activeJob?.name })}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>{Icons.plus('#fff')} Add Entity</span>
        </Btn>
      )}

      {(level === 'entities' || level === 'docs') && allJobsForSwitcher.length > 0 && (
        <Dropdown<number>
          label="Job"
          options={allJobsForSwitcher.map((j) => ({ label: j.name, value: j.id }))}
          value={activeJobId ?? ''}
          onSelect={(v) => { if (v !== '') { setActiveJobId(Number(v)); setActiveEntityId(null); } }}
          minWidth={180}
        />
      )}

      {level === 'docs' && entities.data && (
        <Dropdown<number>
          label="Entity"
          options={entities.data.map((e) => ({
            label: e.entity_name,
            value: e.entity_id,
            sub: e.entity?.entity_type_name ?? undefined,
          }))}
          value={activeEntityId}
          onSelect={(v) => setActiveEntityId(v === '' ? null : Number(v))}
          minWidth={220}
          placeholder="All entities"
        />
      )}

      {level !== 'topGroups' && (() => {
        const folderWebUrl =
          level === 'corpChildren' ? corpGroup?.sharepoint_folder_web_url :
          level === 'jobs'         ? activeGroup?.sharepoint_folder_web_url :
          level === 'entities'     ? activeJob?.sharepoint_folder_web_url :
          level === 'docs'         ? (activeEntityRow?.entity_sharepoint_folder_web_url ?? activeJob?.sharepoint_folder_web_url) :
          null;
        return folderWebUrl ? (
          <a
            href={folderWebUrl}
            target="_blank"
            rel="noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '6px 12px', borderRadius: 6, textDecoration: 'none',
              background: COLORS.blueSoft, color: COLORS.blue,
              border: `1px solid ${COLORS.blue}40`,
              fontSize: 13, fontWeight: 500,
            }}
          >
            {Icons.share(COLORS.blue)} Open in SharePoint
          </a>
        ) : null;
      })()}

      {level !== 'topGroups' && (
        <Btn
          small
          onClick={() => docsFolderId && setShowBulkUpload(true)}
          title={!docsFolderId ? 'No SharePoint folder available' : undefined}
          style={{
            background: COLORS.blueSoft,
            color: docsFolderId ? COLORS.blue : COLORS.mid,
            border: `1px solid ${docsFolderId ? COLORS.blue : COLORS.mid}40`,
            opacity: !docsFolderId ? 0.5 : 1,
            cursor: !docsFolderId ? 'not-allowed' : 'pointer',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            {Icons.plus(docsFolderId ? COLORS.blue : COLORS.mid)} Add Documents
          </span>
        </Btn>
      )}

      {level === 'docs' && (
        <Btn
          small
          onClick={() => setShowAgentPanel((s) => !s)}
          style={{ background: GRADIENTS.aiSoft, color: COLORS.purple, border: `1px solid ${COLORS.purple}40` }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <span className="crm-anim-sparkle" style={{ color: COLORS.purple, lineHeight: 0 }}>{Icons.sparkle(COLORS.purple, 14)}</span>
            Document Assistance
          </span>
        </Btn>
      )}
    </>
  );

  return (
    <div className="crm-anim-fadeUp">
      <PageHeader
        icon={headerIcon}
        title={heading}
        subtitle={headerSubtitle}
        breadcrumb={crumb}
        actions={headerActions}
        accent="brand"
      />

      {showAgentPanel && level === 'docs' && (
        <DocumentAssistancePanel
          entityId={activeEntityId}
          jobId={activeJobId ?? undefined}
          documents={fileItems.map((d) => ({ itemId: d.id, name: d.name, fileType: fileExt(d.name) }))}
          baseApiUrl={baseApiUrl}
          onClose={() => setShowAgentPanel(false)}
        />
      )}

      {/* Level: top-level groups — card grid */}
      {level === 'topGroups' && (
        <>
          <div style={{ marginBottom: 14, position: 'relative' }}>
            <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', lineHeight: 0 }}>
              {Icons.search()}
            </span>
            <input
              type="text"
              value={groupSearch}
              onChange={(e) => setGroupSearch(e.target.value)}
              placeholder="Search client groups..."
              style={{
                width: '100%',
                padding: '11px 14px 11px 38px',
                borderRadius: 10,
                border: `1px solid ${COLORS.border}`,
                fontSize: 14,
                fontFamily: 'inherit',
                outline: 'none',
                color: COLORS.dark,
                background: COLORS.white,
                boxSizing: 'border-box' as const,
                boxShadow: SHADOWS.sm,
              }}
            />
          </div>
          <GroupsTable
            groups={(topGroups.data || []).filter((g) =>
              g.name.toLowerCase().includes(groupSearch.toLowerCase())
            )}
            loading={topGroups.loading}
            error={topGroups.error}
            onSelect={drillFromTopGroup}
            showTypeColumn
            emptyMessage={groupSearch.length > 0 ? 'No groups match your search.' : 'No client groups yet.'}
            onDelete={canEdit ? handleTrashGroup : undefined}
            preparingDeleteId={preparingDelete}
          />
        </>
      )}

      {/* Level: corp children — two stacked sections (family groups, then direct jobs, then docs) */}
      {level === 'corpChildren' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          <Section title="Documents" count={fileItems.length}>
            <DocumentsTable
              documents={fileItems}
              loading={documents.loading}
              error={is404Error(documents.error) ? null : documents.error}
              emptyMessage={is404Error(documents.error) ? 'No SharePoint folder found for this item.' : undefined}
              showEntityColumn={false}
              formatDate={formatDate}
              formatSize={formatSize}
              fileExt={fileExt}
              onDelete={canDelete ? handleTrashDocument : undefined}
              baseApiUrl={baseApiUrl}
            />
          </Section>
        </div>
      )}

      {/* Level: jobs (under active family or standalone group) */}
      {level === 'jobs' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          <Section title="Jobs" count={jobsForActiveGroup.data?.length}>
            <JobsTable
              jobs={jobsForActiveGroup.data || []}
              loading={jobsForActiveGroup.loading}
              error={jobsForActiveGroup.error}
              onSelect={drillIntoJob}
              onDelete={canEdit ? handleTrashJob : undefined}
            />
          </Section>
          <Section title="Documents" count={fileItems.length}>
            <DocumentsTable
              documents={fileItems}
              loading={documents.loading}
              error={is404Error(documents.error) ? null : documents.error}
              emptyMessage={is404Error(documents.error) ? 'No SharePoint folder found for this item.' : undefined}
              showEntityColumn={false}
              formatDate={formatDate}
              formatSize={formatSize}
              fileExt={fileExt}
              onDelete={canDelete ? handleTrashDocument : undefined}
              baseApiUrl={baseApiUrl}
            />
          </Section>
        </div>
      )}

      {/* Level: entities + job-level documents */}
      {level === 'entities' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
          <Section title="Entities" count={entities.data?.length}>
            <EntitiesTable
              rows={entities.data || []}
              loading={entities.loading}
              error={entities.error}
              onSelect={drillIntoEntity}
              onDelete={canEdit ? (entityId, jobId, entityName) => handleTrashEntity(entityId, jobId, entityName) : undefined}
            />
          </Section>
          <Section title="Documents" count={fileItems.length}>
            <DocumentsTable
              documents={fileItems}
              loading={documents.loading}
              error={is404Error(documents.error) ? null : documents.error}
              emptyMessage={is404Error(documents.error) ? 'No SharePoint folder found for this item.' : undefined}
              showEntityColumn={false}
              formatDate={formatDate}
              formatSize={formatSize}
              fileExt={fileExt}
              onDelete={canDelete ? handleTrashDocument : undefined}
              baseApiUrl={baseApiUrl}
            />
          </Section>
        </div>
      )}

      {/* Level: documents */}
      {level === 'docs' && (
        <DocumentsTable
          documents={fileItems}
          loading={documents.loading}
          error={is404Error(documents.error) ? null : documents.error}
          emptyMessage={is404Error(documents.error) ? 'No SharePoint folder found for this item.' : undefined}
          showEntityColumn={!activeEntityRow}
          entityName={activeEntityRow?.entity_name}
          formatDate={formatDate}
          formatSize={formatSize}
          fileExt={fileExt}
          onDelete={canDelete ? handleTrashDocument : undefined}
          baseApiUrl={baseApiUrl}
        />
      )}

      {/* Modals */}
      <CreateGroupModal
        baseApiUrl={baseApiUrl}
        open={modal?.kind === 'add-group'}
        lockedType={modal?.kind === 'add-group' ? modal.lockedType : undefined}
        defaultParentCorpId={modal?.kind === 'add-group' ? modal.defaultParentCorpId : undefined}
        onCreated={handleGroupCreated}
        onClose={() => setModal(null)}
      />
      <CreateJobModal
        baseApiUrl={baseApiUrl}
        open={modal?.kind === 'add-job'}
        groupId={modal?.kind === 'add-job' ? modal.groupId : 0}
        groupName={modal?.kind === 'add-job' ? modal.groupName : undefined}
        onCreated={handleJobCreated}
        onClose={() => setModal(null)}
      />
      <CreateEntityModal
        baseApiUrl={baseApiUrl}
        open={modal?.kind === 'add-entity'}
        jobId={modal?.kind === 'add-entity' ? modal.jobId : 0}
        jobName={modal?.kind === 'add-entity' ? modal.jobName : undefined}
        onCreated={handleEntityCreated}
        onClose={() => setModal(null)}
      />
      <ImportWizardModal
        baseApiUrl={baseApiUrl}
        open={modal?.kind === 'import'}
        onClose={() => setModal(null)}
        onComplete={() => { setModal(null); topGroups.refetch(); }}
      />
      <BulkUploadModal
        baseApiUrl={baseApiUrl}
        open={showBulkUpload}
        folderId={docsFolderId}
        folderName={heading}
        onClose={() => setShowBulkUpload(false)}
        onUploaded={() => documents.refetch()}
      />
      <DeleteConfirmModal
        open={modal?.kind === 'delete'}
        onClose={() => setModal(null)}
        onConfirm={handleDeleteConfirm}
        title={
          modal?.kind === 'delete'
            ? modal.deleteMode === 'corporate-group' ? 'Delete Corporate Group'
            : modal.deleteMode === 'family-group'    ? 'Delete Family Group'
            : modal.deleteMode === 'job'             ? 'Delete Job'
            : modal.deleteMode === 'entity'          ? 'Remove Entity'
            : 'Delete Document'
            : ''
        }
        itemName={modal?.kind === 'delete' ? modal.itemName : ''}
        mode={modal?.kind === 'delete' ? modal.deleteMode : 'document'}
        documentMoveDestination={modal?.kind === 'delete' ? modal.documentMoveDestination : undefined}
        childSummary={modal?.kind === 'delete' ? modal.childSummary : undefined}
      />

      {toast.node}
    </div>
  );
};

// ───────────────────────── Sub-tables / sections ─────────────────────────────

const Section: React.FC<{ title: string; count?: number; children: React.ReactNode }> = ({ title, count, children }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.mid, textTransform: 'uppercase', letterSpacing: 0.6 }}>{title}</div>
      {count != null && (
        <span style={{
          fontSize: 11, fontWeight: 600, color: COLORS.blue, background: COLORS.blueSoft,
          padding: '1px 8px', borderRadius: 999,
        }}>{count}</span>
      )}
    </div>
    {children}
  </div>
);

// 1-2 letter initials derived from the group name for the row avatar.
function groupInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const GroupsTable: React.FC<{
  groups: Group[];
  loading: boolean;
  error: string | null;
  onSelect: (g: Group) => void;
  showTypeColumn?: boolean;
  emptyMessage?: string;
  onDelete?: (g: Group) => void;
  preparingDeleteId?: number | null;
}> = ({ groups, loading, error, onSelect, showTypeColumn, emptyMessage, onDelete, preparingDeleteId }) => {
  if (loading) return <SkeletonTable rows={5} widths={[30, 10, 18, 10, 14, 8]} />;
  if (error) return <ErrorBlock message={error} />;
  if (!groups.length) return (
    <EmptyState
      icon={Icons.users(COLORS.blue, 24)}
      title={emptyMessage ?? 'No groups yet.'}
      compact
    />
  );

  return (
    <div style={{ background: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.border}`, overflow: 'hidden', boxShadow: SHADOWS.sm }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <TH style={{ paddingLeft: 22 }}>Group</TH>
            {showTypeColumn && <TH>Type</TH>}
            <TH>Department</TH>
            <TH>XPM Code</TH>
            <TH>SharePoint</TH>
            {onDelete && <TH style={{ width: 44 }} />}
            <TH style={{ width: 44 }} />
          </tr>
        </thead>
        <tbody className="crm-stagger">
          {groups.map((g) => {
            const isCorp = g.type === 'corporate_group';
            const accent     = isCorp ? COLORS.purple     : COLORS.blue;
            const accentSoft = isCorp ? COLORS.purpleSoft : COLORS.blueSoft;
            return (
              <tr
                key={g.id}
                className="crm-group-row"
                onClick={() => onSelect(g)}
                style={{ ['--row-accent' as 'color']: accent }}
              >
                <TD style={{ padding: '14px 14px 14px 22px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 38, height: 38, borderRadius: 10,
                      background: accentSoft, color: accent,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontWeight: 700, fontSize: 13, letterSpacing: 0.4, flexShrink: 0,
                    }}>{groupInitials(g.name)}</div>
                    <div style={{
                      fontSize: 14, fontWeight: 700, color: COLORS.dark,
                      lineHeight: 1.3, minWidth: 0,
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}>{g.name}</div>
                  </div>
                </TD>
                {showTypeColumn && <TD><GroupTypeBadge type={g.type} /></TD>}
                <TD>{g.department_name ?? '—'}</TD>
                <TD style={{ color: COLORS.mid, fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace' }}>
                  {g.xpm_code ?? ''}
                </TD>
                <TD>
                  {g.sharepoint_folder_id ? (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      fontSize: 11, fontWeight: 600,
                      padding: '4px 10px', borderRadius: 999,
                      background: COLORS.greenSoft, color: COLORS.green,
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: COLORS.green }} /> Linked
                    </span>
                  ) : (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      fontSize: 11, fontWeight: 600,
                      padding: '4px 10px', borderRadius: 999,
                      background: COLORS.orangeSoft, color: COLORS.orange,
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: COLORS.orange }} /> No folder
                    </span>
                  )}
                </TD>
                {onDelete && (
                  <TD>
                    {preparingDeleteId === g.id ? (
                      <span style={{ fontSize: 12, color: COLORS.mid }}>…</span>
                    ) : (
                      <span className="crm-row-actions">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onDelete(g); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '6px 8px', color: COLORS.mid, borderRadius: 6, lineHeight: 0 }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.redSoft; e.currentTarget.style.color = COLORS.red; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = COLORS.mid; }}
                          title="Delete"
                        >
                          {Icons.trash()}
                        </button>
                      </span>
                    )}
                  </TD>
                )}
                <TD style={{ color: COLORS.mid }}>{Icons.chevRight(COLORS.mid)}</TD>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const JobsTable: React.FC<{
  jobs: Job[];
  loading: boolean;
  error: string | null;
  onSelect: (j: Job) => void;
  emptyMessage?: string;
  onDelete?: (j: Job) => void;
}> = ({ jobs, loading, error, onSelect, emptyMessage, onDelete }) => {
  if (loading) return <SkeletonTable rows={3} widths={[28, 14, 8, 14, 12, 14, 6]} />;
  if (error) return <ErrorBlock message={error} />;
  if (!jobs.length) return (
    <EmptyState
      icon={Icons.building(COLORS.blue, 24)}
      title={emptyMessage ?? 'No jobs for this group.'}
      compact
    />
  );
  return (
    <div style={{ background: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.border}`, overflow: 'hidden', boxShadow: SHADOWS.sm }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <TH>Job / Year</TH>
            <TH>Type</TH>
            <TH>Year</TH>
            <TH>Status</TH>
            <TH>SP Folder</TH>
            <TH>Updated</TH>
            {onDelete && <TH style={{ width: 36 }} />}
            <TH style={{ width: 40 }} />
          </tr>
        </thead>
        <tbody className="crm-stagger">
          {jobs.map((j) => (
            <tr
              key={j.id}
              className="crm-row-hover"
              style={{ cursor: 'pointer' }}
              onClick={() => onSelect(j)}
              onMouseEnter={(e) => (e.currentTarget.style.background = COLORS.pageBg)}
              onMouseLeave={(e) => (e.currentTarget.style.background = '')}
            >
              <TD style={{ fontWeight: 600 }}>{j.name}</TD>
              <TD>{j.job_type_name ?? '—'}</TD>
              <TD>{j.year ?? ''}</TD>
              <TD style={{ textTransform: 'capitalize' }}>{j.status.replace('_', ' ')}</TD>
              <TD>
                {j.sharepoint_folder_id
                  ? <span style={{ color: COLORS.green, fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: COLORS.green }} /> Linked
                    </span>
                  : <span style={{ color: COLORS.orange, fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: COLORS.orange }} /> No folder
                    </span>}
              </TD>
              <TD style={{ color: COLORS.mid, fontSize: 12 }}>{new Date(j.updated_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</TD>
              {onDelete && (
                <TD>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDelete(j); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', color: COLORS.mid, borderRadius: 4, lineHeight: 0 }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.redSoft; e.currentTarget.style.color = COLORS.red; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = COLORS.mid; }}
                    title="Delete"
                  >
                    {Icons.trash()}
                  </button>
                </TD>
              )}
              <TD>{Icons.chevRight(COLORS.mid)}</TD>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const EntitiesTable: React.FC<{
  rows: NonNullable<ReturnType<typeof useEntities>['data']>;
  loading: boolean;
  error: string | null;
  onSelect: (id: number) => void;
  onDelete?: (entityId: number, jobId: number, entityName: string) => void;
}> = ({ rows, loading, error, onSelect, onDelete }) => {
  if (loading) return <SkeletonTable rows={3} widths={[35, 14, 18, 18, 6]} />;
  if (error) return <ErrorBlock message={error} />;
  if (!rows.length) return (
    <EmptyState
      icon={Icons.users(COLORS.blue, 24)}
      title="No entities linked to this job."
      description="Use “Add Entity” to link the entities involved in this job."
      compact
    />
  );
  return (
    <div style={{ background: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.border}`, overflow: 'hidden', boxShadow: SHADOWS.sm }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <TH>Entity</TH>
            <TH>Type</TH>
            <TH>ABN</TH>
            <TH>Linked since</TH>
            {onDelete && <TH style={{ width: 36 }} />}
            <TH style={{ width: 40 }} />
          </tr>
        </thead>
        <tbody className="crm-stagger">
          {rows.map((row) => (
            <tr
              key={`${row.job_id}-${row.entity_id}`}
              className="crm-row-hover"
              style={{ cursor: 'pointer' }}
              onClick={() => onSelect(row.entity_id)}
              onMouseEnter={(e) => (e.currentTarget.style.background = COLORS.pageBg)}
              onMouseLeave={(e) => (e.currentTarget.style.background = '')}
            >
              <TD style={{ color: COLORS.blue, fontWeight: 600 }}>{row.entity_name}</TD>
              <TD>{row.entity?.entity_type_name ? <TypeBadge type={row.entity.entity_type_name} /> : '—'}</TD>
              <TD style={{ color: COLORS.mid, fontSize: 12 }}>{row.entity?.australian_business_number ?? ''}</TD>
              <TD style={{ color: COLORS.mid, fontSize: 12 }}>{new Date(row.date_from).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}</TD>
              {onDelete && (
                <TD>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDelete(row.entity_id, row.job_id, row.entity_name); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', color: COLORS.mid, borderRadius: 4, lineHeight: 0 }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.redSoft; e.currentTarget.style.color = COLORS.red; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = COLORS.mid; }}
                    title="Remove entity"
                  >
                    {Icons.trash()}
                  </button>
                </TD>
              )}
              <TD>{Icons.chevRight(COLORS.mid)}</TD>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DocumentsTable: React.FC<{
  documents: FolderItem[];
  loading: boolean;
  error: string | null;
  showEntityColumn: boolean;
  entityName?: string;
  formatDate: (iso?: string) => string;
  formatSize: (b?: number) => string;
  fileExt: (name: string) => string;
  onDelete?: (d: FolderItem) => void;
  emptyMessage?: string;
  baseApiUrl?: string;
}> = ({ documents, loading, error, showEntityColumn, entityName, formatDate, formatSize, fileExt, onDelete, emptyMessage, baseApiUrl }) => {
  // null = status not yet fetched (show Stored as default); Set = fetched result
  const [indexedIds, setIndexedIds] = useState<Set<string> | null>(null);
  const [indexingIds, setIndexingIds] = useState<Set<string>>(new Set());
  const [reindexingIds, setReindexingIds] = useState<Set<string>>(new Set());
  const [unindexingIds, setUnindexingIds] = useState<Set<string>>(new Set());

  const docIdKey = documents.map((d) => d.id).join(',');

  useEffect(() => {
    setIndexedIds(null);
    if (!baseApiUrl || !docIdKey) return;
    let cancelled = false;
    const ids = docIdKey.split(',').filter(Boolean);
    axios
      .post<{ indexed: string[] }>(`${baseApiUrl}/api/documents/vector-status`, { ids })
      .then(({ data }) => { if (!cancelled) setIndexedIds(new Set(data.indexed)); })
      .catch(() => { /* leave null — defaults all to Stored */ });
    return () => { cancelled = true; };
  }, [baseApiUrl, docIdKey]);

  async function handleIndex(docId: string) {
    if (!baseApiUrl) return;
    setIndexingIds((prev) => new Set([...prev, docId]));
    try {
      await axios.post(
        `${baseApiUrl}/api/documents/index-batch`,
        { ids: [docId] },
        { validateStatus: (s) => s === 200 || s === 207 },
      );
      setIndexedIds((prev) => new Set([...(prev ?? []), docId]));
    } catch {
      // silent — badge stays as "Not indexed" so user can retry
    } finally {
      setIndexingIds((prev) => { const n = new Set(prev); n.delete(docId); return n; });
    }
  }

  async function handleReindex(docId: string) {
    if (!baseApiUrl) return;
    setReindexingIds((prev) => new Set([...prev, docId]));
    try {
      await axios.post(
        `${baseApiUrl}/api/documents/index-batch`,
        { ids: [docId], force: true },
        { validateStatus: (s) => s === 200 || s === 207 },
      );
    } catch {
      // silent — user can retry
    } finally {
      setReindexingIds((prev) => { const n = new Set(prev); n.delete(docId); return n; });
    }
  }

  async function handleUnindex(docId: string) {
    if (!baseApiUrl) return;
    setUnindexingIds((prev) => new Set([...prev, docId]));
    try {
      await axios.delete(`${baseApiUrl}/api/documents/${docId}/index`);
      setIndexedIds((prev) => { const n = new Set(prev ?? []); n.delete(docId); return n; });
    } catch {
      // silent — badge stays as indexed so user can retry
    } finally {
      setUnindexingIds((prev) => { const n = new Set(prev); n.delete(docId); return n; });
    }
  }

  if (loading) return <SkeletonTable rows={4} widths={[40, 14, 10, 12, 14, 8]} />;
  if (error) return <ErrorBlock message={error} />;
  if (!documents.length) return (
    <EmptyState
      icon={Icons.inbox(COLORS.blue, 32)}
      title={emptyMessage ?? 'No documents in this folder.'}
      description="Upload documents or sync from SharePoint to populate this view."
      compact
    />
  );
  return (
    <div style={{ background: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.border}`, overflow: 'hidden', boxShadow: SHADOWS.sm }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <TH>Document</TH>
            {showEntityColumn && <TH>Entity</TH>}
            <TH>Type</TH>
            <TH>Status</TH>
            <TH>Modified</TH>
            <TH>Size</TH>
            {onDelete && <TH style={{ width: 36 }} />}
          </tr>
        </thead>
        <tbody className="crm-stagger">
          {documents.map((d) => {
            const ext = fileExt(d.name);
            const isIndexed = indexedIds === null || indexedIds.has(d.id);
            const isIndexConfirmed = indexedIds !== null && indexedIds.has(d.id);
            const isIndexing = indexingIds.has(d.id);
            const isReindexing = reindexingIds.has(d.id);
            const isUnindexing = unindexingIds.has(d.id);
            return (
              <tr
                key={d.id}
                className="crm-row-hover"
                onMouseEnter={(e) => (e.currentTarget.style.background = COLORS.pageBg)}
                onMouseLeave={(e) => (e.currentTarget.style.background = '')}
              >
                <TD>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {Icons.file(ext)}
                    {d.webUrl ? (
                      <a href={d.webUrl} target="_blank" rel="noreferrer" style={{ color: COLORS.dark, textDecoration: 'none', fontWeight: 500 }}>{d.name}</a>
                    ) : <span style={{ fontWeight: 500 }}>{d.name}</span>}
                  </span>
                </TD>
                {showEntityColumn && <TD style={{ color: COLORS.mid, fontSize: 12 }}>{entityName ?? '—'}</TD>}
                <TD style={{ textTransform: 'uppercase', fontSize: 11, color: COLORS.mid }}>{ext}</TD>
                <TD>
                  {isIndexed ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <StatusBadge status="Stored" />
                      {baseApiUrl && isIndexConfirmed && (
                        <button
                          type="button"
                          disabled={isReindexing}
                          onClick={() => handleReindex(d.id)}
                          style={{
                            fontSize: 10, padding: '2px 7px', borderRadius: 4,
                            background: COLORS.blueSoft, color: COLORS.blue,
                            border: `1px solid ${COLORS.blue}40`,
                            cursor: isReindexing ? 'default' : 'pointer',
                            fontWeight: 600, opacity: isReindexing ? 0.6 : 1,
                          }}
                        >
                          {isReindexing ? 'Re-indexing…' : 'Re-index'}
                        </button>
                      )}
                      {baseApiUrl && isIndexConfirmed && (
                        <button
                          type="button"
                          disabled={isUnindexing}
                          onClick={() => handleUnindex(d.id)}
                          style={{
                            fontSize: 10, padding: '2px 7px', borderRadius: 4,
                            background: COLORS.orangeSoft, color: COLORS.orange,
                            border: `1px solid ${COLORS.orange}40`,
                            cursor: isUnindexing ? 'default' : 'pointer',
                            fontWeight: 600, opacity: isUnindexing ? 0.6 : 1,
                          }}
                        >
                          {isUnindexing ? 'Removing…' : 'Unindex'}
                        </button>
                      )}
                    </span>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <StatusBadge status="Not indexed" />
                      {baseApiUrl && (
                        <button
                          type="button"
                          disabled={isIndexing}
                          onClick={() => handleIndex(d.id)}
                          style={{
                            fontSize: 10, padding: '2px 7px', borderRadius: 4,
                            background: COLORS.blueSoft, color: COLORS.blue,
                            border: `1px solid ${COLORS.blue}40`,
                            cursor: isIndexing ? 'default' : 'pointer',
                            fontWeight: 600, opacity: isIndexing ? 0.6 : 1,
                          }}
                        >
                          {isIndexing ? 'Indexing…' : 'Index'}
                        </button>
                      )}
                    </span>
                  )}
                </TD>
                <TD style={{ color: COLORS.mid, fontSize: 12 }}>{formatDate(d.lastModified)}</TD>
                <TD style={{ color: COLORS.mid, fontSize: 12 }}>{formatSize(d.size)}</TD>
                {onDelete && (
                  <TD>
                    <button
                      type="button"
                      onClick={() => onDelete(d)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 6px', color: COLORS.mid, borderRadius: 4, lineHeight: 0 }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = COLORS.redSoft; e.currentTarget.style.color = COLORS.red; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = COLORS.mid; }}
                      title="Delete"
                    >
                      {Icons.trash()}
                    </button>
                  </TD>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ClientsView;
